import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { db, functions } from '../firebase/config';
import { getCurrentUser, User } from '../services/authService';
import { httpsCallable } from 'firebase/functions';
import TopBar from '../components/TopBar';
import Button from '../components/Button';
import Input from '../components/Input';
import PlaceSearchInput from '../components/PlaceSearchInput';

const GOOGLE_API_KEY = 'AIzaSyBUKsrmx9qFndhqsS767pqnHDMgHrbcZvk'; 
if (!GOOGLE_API_KEY) {
    console.error("FATAL ERROR: Google API Key not found in environment variables!");
}

interface PlaceResult {
    place_id: string;
    name: string;
    formatted_address: string;
}

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  padding-top: 20px;
`;

const PageTitle = styled.h1`
  font-size: 38px;
  font-weight: 800;
  margin-bottom: 16px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
  width: 100%;
  overflow-x: hidden;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  white-space: nowrap;
  &:hover { text-decoration: underline; }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const BusinessSection = styled.div`
  margin-bottom: 40px;
  width: 100%;
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: 24px;
  padding: 32px;
  margin-bottom: 24px;
  width: 100%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const DetailsContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ButtonContainer = styled.div`
  margin-top: 30px;
  display: flex;
  justify-content: flex-end;
`;

const SuccessMessage = styled.p`
  color: #43a047; margin-top: 15px; font-size: 14px; text-align: center;
`;
const ErrorMessage = styled.p`
  color: #e53935; margin-top: 15px; font-size: 14px; text-align: center;
`;

const PlacesDropdown = styled.div`
  position: relative; margin-bottom: 20px;
`;
const DropdownResults = styled.div<{ isVisible: boolean }>`
  position: absolute; top: 100%; left: 0; right: 0; background-color: white;
  border: 1px solid #ddd; border-radius: 8px; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 10; max-height: 200px; overflow-y: auto; display: ${props => props.isVisible ? 'block' : 'none'};
`;
const DropdownItem = styled.div`
  padding: 10px 15px; cursor: pointer; &:hover { background-color: #f5f5f5; }
`;

const RevealedFields = styled.div<{ isVisible: boolean }>`
    margin-top: 30px; display: ${props => props.isVisible ? 'block' : 'none'};
    animation: ${props => props.isVisible ? 'fadeIn 0.5s ease-in-out' : 'none'};
    @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
`;

const LoadingPlaceholder = styled.div`
    display: flex; justify-content: center; align-items: center;
    min-height: 200px; font-size: 18px; color: #888; width: 100%;
`;


const AddBusinessPage: React.FC = () => {
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loadingUser, setLoadingUser] = useState(true);
    const [saving, setSaving] = useState(false);
    const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPlaceId, setSelectedPlaceId] = useState('');
    const [selectedPlaceName, setSelectedPlaceName] = useState('');
    const [selectedPlaceAddress, setSelectedPlaceAddress] = useState('');
    const [streetAddress, setStreetAddress] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [businessEmail, setBusinessEmail] = useState('');
    const [placesResults, setPlacesResults] = useState<PlaceResult[]>([]);
    const [showPlacesDropdown, setShowPlacesDropdown] = useState(false);
    const [placesLoading, setPlacesLoading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isBusinessAlreadyRegistered, setIsBusinessAlreadyRegistered] = useState(false);
    const [selectedPlacePhoto, setSelectedPlacePhoto] = useState<google.maps.places.PlacePhoto | null>(null);
    const autocompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Function to check if business is already registered
    const checkIfBusinessExists = async (placeId: string): Promise<{exists: boolean; businessName?: string}> => {
        try {
            const businessesRef = collection(db, 'businesses');
            const duplicateQuery = query(businessesRef, where('gmbId', '==', placeId));
            const duplicateSnapshot = await getDocs(duplicateQuery);
            
            if (!duplicateSnapshot.empty) {
                const existingBusiness = duplicateSnapshot.docs[0].data();
                return {
                    exists: true,
                    businessName: existingBusiness.name
                };
            }
            return { exists: false };
        } catch (error) {
            console.error('Error checking if business exists:', error);
            return { exists: false };
        }
    };

    // Function to download Google Places image via Cloud Function (to avoid CORS)
    const downloadGooglePlacesImageViaFunction = async (photo: google.maps.places.PlacePhoto, businessId: string, fileName: string): Promise<string | null> => {
        try {
            console.log('Downloading Google Places image via Cloud Function...');
            
            const photoUrl = photo.getUrl({ 
                maxWidth: 800,
                maxHeight: 800
            });
            
            const downloadImage = httpsCallable(functions, 'downloadGooglePlacesImage');
            
            const result = await downloadImage({
                photoUrl,
                businessId,
                fileName
            });
            
            const data = result.data as { success: boolean; logoUrl: string; filePath: string };
            
            if (data.success) {
                console.log('Image downloaded successfully:', data.logoUrl);
                return data.logoUrl;
            } else {
                console.error('Image download failed');
                return null;
            }
        } catch (error) {
            console.error('Error downloading Google Places image via function:', error);
            // Fallback: Try local approach for development
            console.log('Falling back to local image processing...');
            return await downloadGooglePlacesImageLocal(photo, fileName);
        }
    };

    // Fallback function for local development or when Cloud Function is not available
    const downloadGooglePlacesImageLocal = async (photo: google.maps.places.PlacePhoto, fileName: string): Promise<string | null> => {
        try {
            console.log('Attempting local image processing...');
            
            return new Promise((resolve) => {
                // Create an image element
                const img = new Image();
                // Remove crossOrigin to avoid CORS issues
                // img.crossOrigin = 'anonymous';
                
                img.onload = async () => {
                    try {
                        console.log('Image loaded, dimensions:', img.width, 'x', img.height);
                        
                        // Create canvas to process the image
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        
                        // Set canvas size (maintain aspect ratio, max 800px)
                        const maxSize = 800;
                        let { width, height } = img;
                        
                        if (width > height) {
                            if (width > maxSize) {
                                height = (height * maxSize) / width;
                                width = maxSize;
                            }
                        } else {
                            if (height > maxSize) {
                                width = (width * maxSize) / height;
                                height = maxSize;
                            }
                        }
                        
                        canvas.width = width;
                        canvas.height = height;
                        
                        if (ctx) {
                            // Draw and compress the image
                            ctx.drawImage(img, 0, 0, width, height);
                            
                            // Convert canvas to blob with compression
                            canvas.toBlob(async (blob) => {
                                if (blob) {
                                    try {
                                        const file = new File([blob], fileName, { type: 'image/jpeg' });
                                        console.log('Image processed successfully, final size:', file.size, 'bytes');
                                        
                                        // Upload using the existing uploadBusinessLogo function
                                        // We need the businessId, so we'll return null here and handle upload separately
                                        resolve('processed'); // Signal that processing was successful
                                    } catch (uploadError) {
                                        console.error('Error uploading processed image:', uploadError);
                                        resolve(null);
                                    }
                                } else {
                                    console.error('Failed to convert canvas to blob');
                                    resolve(null);
                                }
                            }, 'image/jpeg', 0.85); // 85% quality for good balance
                        } else {
                            console.error('Failed to get canvas context');
                            resolve(null);
                        }
                    } catch (error) {
                        console.error('Error processing image:', error);
                        resolve(null);
                    }
                };
                
                img.onerror = (error) => {
                    console.error('Failed to load image:', error);
                    resolve(null);
                };
                
                // Get the photo URL with appropriate size
                const photoUrl = photo.getUrl({ 
                    maxWidth: 800,
                    maxHeight: 800
                });
                
                console.log('Loading image from URL:', photoUrl);
                img.src = photoUrl;
            });
        } catch (error) {
            console.error('Error in local image processing:', error);
            return null;
        }
    };

    useEffect(() => {
        const loadGoogleMapsScript = () => {
             if (!window.google && !document.querySelector('script[src*="maps.googleapis.com/maps/api"]')) {
                 const script = document.createElement('script');
                 script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
                 script.async = true; script.defer = true;
                 script.onload = () => setGoogleMapsLoaded(true);
                 script.onerror = () => { console.error("Failed to load Google Maps API script."); setError("Could not load Google Maps functionality."); }
                 document.head.appendChild(script);
             } else if (window.google && window.google.maps) { setGoogleMapsLoaded(true); }
         };
         if (GOOGLE_API_KEY) { loadGoogleMapsScript(); } else { setError("Google API Key is missing."); }
    }, []);
    useEffect(() => {
        const fetchUser = async () => {
             setLoadingUser(true);
             try {
                 const user = await getCurrentUser();
                 setCurrentUser(user);
                 if (!user) navigate('/login');
             } catch (err) { console.error("Error fetching current user:", err); setError("Could not verify user session."); navigate('/login'); }
             finally { setLoadingUser(false); }
         };
         fetchUser();
    }, [navigate]);
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
             if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
                 inputRef.current && !inputRef.current.contains(event.target as Node)) {
                 setShowPlacesDropdown(false);
             }
         };
         document.addEventListener('mousedown', handleClickOutside);
         return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // --- Handlers ---
    const handleNavigateToMyAccount = () => navigate('/dashboard');
    const handleNavigateToMyBusinesses = () => navigate('/businesses');

    const handleGooglePlacesSearch = (query: string) => {
        setSearchQuery(query); setShowDetails(false); setSelectedPlaceId('');
        setSelectedPlaceName(''); setSelectedPlaceAddress(''); setBusinessEmail(''); 
        setSelectedPlacePhoto(null);
        // Reset address components
        setStreetAddress('');
        setCity('');
        setState('');
        setZipCode('');
        // Reset business registration status
        setIsBusinessAlreadyRegistered(false);
        setError(''); // Clear any existing errors
        if (autocompleteTimeoutRef.current) clearTimeout(autocompleteTimeoutRef.current);
        if (!query) { setPlacesResults([]); setShowPlacesDropdown(false); return; }
        setPlacesLoading(true); setShowPlacesDropdown(true);
        autocompleteTimeoutRef.current = setTimeout(() => {
            if (!googleMapsLoaded || !window.google?.maps?.places) return;
            try {
                const service = new window.google.maps.places.AutocompleteService();
                service.getPlacePredictions({ input: query, types: ['establishment'] }, (preds, status) => {
                    setPlacesLoading(false);
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && preds) {
                        setPlacesResults(preds.map(p => ({ place_id: p.place_id, name: p.structured_formatting.main_text, formatted_address: p.structured_formatting.secondary_text || p.description })));
                    } else { setPlacesResults([]); }
                });
            } catch (err) { console.error("Autocomplete error:", err); setPlacesLoading(false); setPlacesResults([]); }
        }, 300);
    };
    const handleGooglePlacesSelect = (placeId: string) => {
      setError('');
      setSelectedPlacePhoto(null);

      if (!googleMapsLoaded || !window.google?.maps?.places) {
          setError("Google Places API not ready");
          setShowPlacesDropdown(false);
          return;
      }
      try {
          const service = new window.google.maps.places.PlacesService(document.createElement('div'));
          service.getDetails({ 
              placeId, 
              fields: ['name', 'formatted_address', 'place_id', 'photos', 'address_components'] 
          }, (place, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
                  const name = place.name || '';
                  const placeIdValue = place.place_id || '';
                  
                  setSelectedPlaceId(placeIdValue);
                  setSelectedPlaceName(name);
                  setSelectedPlaceAddress(place.formatted_address || '');
                  setSearchQuery(name); 
                  
                  // Check if business already exists
                  checkIfBusinessExists(placeIdValue).then((result) => {
                      if (result.exists) {
                          setIsBusinessAlreadyRegistered(true);
                          setError(`This business "${result.businessName}" is already registered on the platform.`);
                      } else {
                          setIsBusinessAlreadyRegistered(false);
                          setError(''); // Clear any previous error
                      }
                  }).catch((error) => {
                      console.error('Error checking business existence:', error);
                      // Don't block the user, just log the error
                      setIsBusinessAlreadyRegistered(false);
                  });
                  
                  // Parse address components
                  let street = '';
                  let cityName = '';
                  let stateName = '';
                  let zip = '';
                  
                  if (place.address_components) {
                      place.address_components.forEach(component => {
                          const types = component.types;
                          
                          if (types.includes('street_number')) {
                              street = component.long_name + ' ';
                          }
                          if (types.includes('route')) {
                              street += component.long_name;
                          }
                          if (types.includes('locality') || types.includes('sublocality')) {
                              cityName = component.long_name;
                          }
                          if (types.includes('administrative_area_level_1')) {
                              stateName = component.short_name; // Use short_name for state abbreviation
                          }
                          if (types.includes('postal_code')) {
                              zip = component.long_name;
                          }
                      });
                  }
                  
                  // Set the parsed address components
                  setStreetAddress(street.trim());
                  setCity(cityName);
                  setState(stateName);
                  setZipCode(zip);
                  
                  if (place.photos && place.photos.length > 0) {
                      // Store the photo object for processing
                      const photo = place.photos[0];
                      setSelectedPlacePhoto(photo);
                      console.log("Found photo:", photo);
                  } else {
                      console.log("No photos found for this place.");
                      setSelectedPlacePhoto(null);
                  }

                  setShowDetails(true);
                  setShowPlacesDropdown(false);
                  setPlacesResults([]);
              } else {
                  setError("Could not retrieve place details. Status: " + status);
                  setShowDetails(false);
                  setSelectedPlacePhoto(null);
              }
          });
      } catch (err) {
          console.error("Place details error:", err);
          setError("Failed to get place details");
          setShowDetails(false);
          setSelectedPlacePhoto(null);
      }
  };

    const handleAddBusiness = async () => {
        setError(''); setSuccess('');
        if (!currentUser?.id) {
            setError("User session invalid. Please log in again.");
            return;
        }
        if (!selectedPlaceId || !selectedPlaceName) { setError("Please select a business using the search."); return; }
        if (!businessEmail.trim() || !/\S+@\S+\.\S+/.test(businessEmail)) { setError('Please enter a valid email address for the business.'); return; }

        setSaving(true);
        try {
          // Check if business already exists using Google Places ID
          console.log('Checking for duplicate business with place ID:', selectedPlaceId);
          const businessesRef = collection(db, 'businesses');
          const duplicateQuery = query(businessesRef, where('gmbId', '==', selectedPlaceId));
          const duplicateSnapshot = await getDocs(duplicateQuery);
          
          if (!duplicateSnapshot.empty) {
            const existingBusiness = duplicateSnapshot.docs[0].data();
            setError(`This business "${existingBusiness.name}" has already been added to the platform. Each business can only be registered once.`);
            setSaving(false);
            return;
          }
          
          // First create the business document to get the ID
          const newBusinessData = {
              name: selectedPlaceName,
              address: selectedPlaceAddress, // Full formatted address
              street: streetAddress,
              city: city,
              state: state,
              zip: zipCode,
              email: businessEmail.trim(),
              gmbId: selectedPlaceId,
              managerId: currentUser.id,
              status: "pending",
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              phone: "",
              website: "",
              logoUrl: "", // Will be updated after image upload
              businessInfo: { gmbId: selectedPlaceId },
          };
          
          const docRef = await addDoc(businessesRef, newBusinessData);
          const businessId = docRef.id;
          console.log("Business added with ID: ", businessId);

          // Download and upload the Google Places image if available
          let finalLogoUrl = "";
          if (selectedPlacePhoto) {
              try {
                  console.log("Processing Google Places image...");
                  const imageFileName = `${selectedPlaceName.replace(/[^a-zA-Z0-9]/g, '_')}_logo.jpg`;
                  
                  const logoResult = await downloadGooglePlacesImageViaFunction(
                      selectedPlacePhoto,
                      businessId,
                      imageFileName
                  );
                  
                  if (logoResult) {
                      finalLogoUrl = logoResult;
                      console.log("Image uploaded successfully via Cloud Function:", finalLogoUrl);
                      
                      // Update the business document with the uploaded logo URL
                      await updateDoc(doc(db, 'businesses', businessId), {
                          logoUrl: finalLogoUrl,
                          updatedAt: serverTimestamp()
                      });
                  } else {
                      console.warn("Failed to upload image via Cloud Function");
                  }
              } catch (imageError) {
                  console.error("Error handling business image:", imageError);
                  // Continue without the image - don't fail the entire process
              }
          }

          console.log("Address components:", { street: streetAddress, city, state, zip: zipCode });

          const userRef = doc(db, 'users', currentUser.id);
          const userSnap = await getDoc(userRef);
           if (userSnap.exists()) {
              const userData = userSnap.data();
              if (userData.role !== 'manager') {
                  console.log(`User ${currentUser.id} role is ${userData.role}. Updating to manager.`);
                  await updateDoc(userRef, {
                      role: 'manager',
                      updatedAt: serverTimestamp()
                  });
                   setCurrentUser(prev => prev ? {...prev, role: 'manager'} : null);
                   console.log(`User ${currentUser.id} role updated to manager.`);
              } else {
                  console.log(`User ${currentUser.id} role is already manager.`);
              }
          } else {
              console.warn(`User document ${currentUser.id} not found. Cannot update role.`);
          }


          setSuccess(`Business "${selectedPlaceName}" added successfully! Redirecting...`);
          setSearchQuery('');
          setSelectedPlaceId('');
          setSelectedPlaceName('');
          setSelectedPlaceAddress('');
          setBusinessEmail('');
          setSelectedPlacePhoto(null);
          // Reset address components
          setStreetAddress('');
          setCity('');
          setState('');
          setZipCode('');
          setShowDetails(false);
          setPlacesResults([]);
          setTimeout(() => navigate('/businesses'), 2500);

      } catch (err) {
          console.error('Error during add business or role update:', err);
          if (err instanceof Error) {
               setError(`Failed to complete operation: ${err.message}`);
          } else {
               setError('An unexpected error occurred while adding the business or updating your role.');
          }
      } finally {
          setSaving(false);
      }
    };


    const renderContent = () => {
        if (loadingUser) return <LoadingPlaceholder>Loading...</LoadingPlaceholder>;
        return (
            <>
                <PageTitle>Add New Business</PageTitle>
                <Breadcrumbs>
                    <BreadcrumbLink onClick={handleNavigateToMyAccount}>My Account</BreadcrumbLink>
                    <BreadcrumbSeparator>/</BreadcrumbSeparator>
                    <BreadcrumbLink onClick={handleNavigateToMyBusinesses}>Businesses</BreadcrumbLink>
                    <BreadcrumbSeparator>/</BreadcrumbSeparator>
                    <span>Add Business</span>
                </Breadcrumbs>
                <BusinessSection>
                    <FormContainer>
                        <PlacesDropdown ref={dropdownRef}>
                            <PlaceSearchInput
                                value={searchQuery}
                                onChange={(e) => handleGooglePlacesSearch(e.target.value)}
                                onFocusAction={() => setShowPlacesDropdown(!!searchQuery && placesResults.length > 0)} // Show only if results or loading
                                placeholder="Search for the business on Google"
                                name="googlePlacesSearch"
                                label="Business Name"
                                secondary
                            />
                            <DropdownResults isVisible={showPlacesDropdown}>
                                {placesLoading ? ( <DropdownItem>Loading...</DropdownItem> ) :
                                 placesResults.length > 0 ? ( placesResults.map((place) => ( <DropdownItem key={place.place_id} onClick={() => handleGooglePlacesSelect(place.place_id)}> <strong>{place.name}</strong> <div>{place.formatted_address}</div> </DropdownItem> )) ) :
                                 ( searchQuery && !placesLoading && <DropdownItem>No results found for "{searchQuery}"</DropdownItem> )}
                            </DropdownResults>
                        </PlacesDropdown>
                        <RevealedFields isVisible={showDetails}>
                            <DetailsContainer>
                                <Input 
                                    type="text" 
                                    value={streetAddress} 
                                    onChange={() => {}} 
                                    placeholder="Street Address" 
                                    name="streetAddress" 
                                    label="Street Address" 
                                    readOnly 
                                    secondary 
                                />
                                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                    <div style={{ flex: '2', minWidth: '200px' }}>
                                        <Input 
                                            type="text" 
                                            value={city} 
                                            onChange={() => {}} 
                                            placeholder="City" 
                                            name="city" 
                                            label="City" 
                                            readOnly 
                                            secondary 
                                        />
                                    </div>
                                    <div style={{ flex: '1', minWidth: '100px' }}>
                                        <Input 
                                            type="text" 
                                            value={state} 
                                            onChange={() => {}} 
                                            placeholder="State" 
                                            name="state" 
                                            label="State" 
                                            readOnly 
                                            secondary 
                                        />
                                    </div>
                                    <div style={{ flex: '1', minWidth: '120px' }}>
                                        <Input 
                                            type="text" 
                                            value={zipCode} 
                                            onChange={() => {}} 
                                            placeholder="ZIP Code" 
                                            name="zipCode" 
                                            label="ZIP Code" 
                                            readOnly 
                                            secondary 
                                        />
                                    </div>
                                </div>
                                <Input 
                                    type="text" 
                                    value={selectedPlaceAddress} 
                                    onChange={() => {}} 
                                    placeholder="Full Address" 
                                    name="fullAddress" 
                                    label="Full Address" 
                                    readOnly 
                                    secondary 
                                />
                                <Input 
                                    type="email" 
                                    value={businessEmail} 
                                    onChange={(e) => setBusinessEmail(e.target.value)} 
                                    placeholder="Required for contact" 
                                    name="businessEmail" 
                                    label="Business Email Address" 
                                    required 
                                    secondary 
                                    error={error.toLowerCase().includes('email') ? error : undefined} 
                                />
                            </DetailsContainer>
                        </RevealedFields>
                        {error && !error.toLowerCase().includes('email') && <ErrorMessage>{error}</ErrorMessage>}
                        {success && <SuccessMessage>{success}</SuccessMessage>}
                    </FormContainer>
                    <RevealedFields isVisible={showDetails}>                            
                        <ButtonContainer>
                            <Button 
                                text={isBusinessAlreadyRegistered ? "Already Registered" : "Add"} 
                                onClick={handleAddBusiness} 
                                loading={saving} 
                                disabled={isBusinessAlreadyRegistered}
                                bgColor={isBusinessAlreadyRegistered ? "#ccc" : "#000"} 
                                textColor={isBusinessAlreadyRegistered ? "#666" : "#fff"} 
                                width="auto" 
                                minWidth="150px" 
                            />
                        </ButtonContainer>
                    </RevealedFields>
                </BusinessSection>
            </>
        );
    };    return (
        <>
            <Helmet>
                <title>{APP_NAME} - Add Business</title>
                <meta name="description" content={`${APP_NAME} - Add Business`} />
            </Helmet>
            <Container>
                <TopBar user={currentUser} />
                <ContentWrapper>
                    {renderContent()}
                </ContentWrapper>
            </Container>
        </>
    );
};

export default AddBusinessPage;